'use client';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, HStack, VStack } from '@cbhq/cds-web/layout';
import { NavigationBar } from '@cbhq/cds-web/navigation/NavigationBar';
import { TabNavigation } from '@cbhq/cds-web/tabs/TabNavigation';
import { useRouter, usePathname } from 'next/navigation';
import { useChainId } from 'wagmi';
import { PressableOpacity } from '@cbhq/cds-web/system';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { Modal } from '@cbhq/cds-web/overlays/modal/Modal';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { LogoMark } from '@cbhq/cds-web/icons/LogoMark';
import { css } from '@emotion/css';
import { IconButton } from '@cbhq/cds-web/buttons/IconButton';
import { TextHeadline } from '@cbhq/cds-web/typography';
import { IconName as UiIconName } from '@cbhq/cds-icons';

import { NavbarAccountConnectButton } from '@/onchain/components';
import { getChainById } from '@/onchain/store/supportedChains';
import { NavbarSettingsDropdown } from '@/components/NavbarSettingsDropdown';

import type { ColorScheme } from '@cbhq/cds-common/core/theme';

type NavigationTab = {
  id: string;
  label: string;
  icon: UiIconName;
  href?: string;
  isExternal?: boolean;
};

const NAVBAR_ACCESSIBILITY_LABELS = {
  navigationBar: 'Navigation bar',
  homeButton: 'Home button',
  openNavigationMenuButton: 'Open navigation menu button',
  closeNavigationMenuButton: 'Close navigation menu button',
  darkAndLightModeButton: 'Dark and light mode button',
  accountConnectButton: 'Account connect button',
};

const NavigationModalCss = css`
  top: 59px;
  .cds-modal {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    top: 0;
    > div {
      box-shadow: none !important;
      border: none;
      border-radius: 0;
    }
  }
  [data-testid='modal-overlay-motion'] {
    display: none;
  }
`;

// Module-scope constant — inlined by Next.js at build time, enabling dead-code elimination.
const isTokenizedEquitiesApp = process.env.NEXT_PUBLIC_APP_MODE === 'tokenized-equities';

const NavTabStyles = css`
  cursor: pointer;
  padding: 18px 0;
  border: none;
  background: none;
  border-bottom: 3px solid transparent;
  margin-bottom: -1px;

  &:hover {
    opacity: 0.8;
  }

  &.active {
    border-bottom-color: var(--color-fgPrimary);
  }
`;

type Props = {
  theme: ColorScheme;
  toggleTheme: () => void;
};

export function Navbar({ theme, toggleTheme }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const currentPath = pathname?.split('/')?.at(1);

  const chainId = useChainId();
  const chain = getChainById(chainId.toString());

  const tabs = useMemo(() => {
    // In Tokenized Equities mode, show Explore and Portfolio tabs only.
    // Swap is a Verified Pools flow and is excluded from the TE app.
    if (isTokenizedEquitiesApp) {
      return [
        { id: '', label: 'Explore', icon: 'search' },
        { id: 'portfolio', label: 'Portfolio', icon: 'chartPie' },
      ] as NavigationTab[];
    }

    const baseTabs = [
      { id: 'swap', label: 'Swap', icon: 'arrowsVertical' },
      { id: 'pools', label: 'Pools', icon: 'defi' },
      { id: 'portfolio', label: 'Positions', icon: 'chartPie' },
      {
        id: 'docs',
        label: 'Docs',
        icon: 'book',
        href: process.env.NEXT_PUBLIC_DOCS_URL,
        isExternal: true,
      },
      {
        id: 'about',
        label: 'About',
        icon: 'info',
        href: process.env.NEXT_PUBLIC_ABOUT_URL,
        isExternal: true,
      },
    ];
    return (
      chain?.testnet
        ? [...baseTabs, { id: 'faucet', label: 'Faucet', icon: 'faucetActive' }]
        : baseTabs
    ) as NavigationTab[];
  }, [chain?.testnet]);

  const [selectedTab, setSelectedTab] = useState(currentPath ? currentPath : tabs[0].id);

  // Sync selectedTab with pathname changes (e.g., browser back/forward, external navigation)
  useEffect(() => {
    const newPath = currentPath ?? '';
    setSelectedTab(newPath);
  }, [currentPath]);

  const { isPhone, isPhoneLandscape } = useBreakpoints();

  const showMobileNavbar = useMemo(() => isPhone ?? isPhoneLandscape, [isPhone, isPhoneLandscape]);

  const [visible, setVisible] = useState(false);
  const toggle = useCallback(() => setVisible((prev) => !prev), []);
  const toggleOff = useCallback(() => setVisible(false), []);

  const handleHomeSelect = useCallback(() => {
    setSelectedTab(tabs[0].id);
    void router.push('/');
    toggleOff();
  }, [router, tabs, toggleOff]);

  const handleTabChange = useCallback(
    (tabValue: string) => {
      const thisTab = tabs.find((tab) => tab.id === tabValue);
      if (thisTab?.href) {
        window.open(thisTab.href, '_blank', 'noopener,noreferrer');
      } else {
        setSelectedTab(tabValue);
        void router.push(`/${tabValue}`);
      }
      toggleOff();
    },
    [router, tabs, toggleOff],
  );

  const tabItems = useMemo(() => {
    return tabs.map((tab) => ({
      ...tab,
      handlePress: () => handleTabChange(tab.id),
    }));
  }, [tabs, handleTabChange]);

  useEffect(() => {
    if (!showMobileNavbar) {
      toggleOff();
    }
  }, [toggleOff, showMobileNavbar]);

  // Keyboard navigation handler for tabs (accessibility)
  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent, currentIndex: number) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        handleTabChange(tabs[nextIndex].id);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const prevIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        handleTabChange(tabs[prevIndex].id);
      } else if (e.key === 'Home') {
        e.preventDefault();
        handleTabChange(tabs[0].id);
      } else if (e.key === 'End') {
        e.preventDefault();
        handleTabChange(tabs[tabs.length - 1].id);
      }
    },
    [tabs, handleTabChange],
  );

  // Custom renderer for tab labels to show external link icon
  const renderTabLabel = useCallback((tab: NavigationTab): React.ReactNode => {
    if (tab.isExternal) {
      return (
        <HStack alignItems="center" gap={1}>
          <TextHeadline as="p">{tab.label}</TextHeadline>
          <Icon name="externalLink" size="xs" />
        </HStack>
      );
    }
    return tab.label;
  }, []);

  return (
    <>
      <Box zIndex={3}>
        <NavigationBar
          accessibilityLabel={NAVBAR_ACCESSIBILITY_LABELS.navigationBar}
          paddingTop={0}
          paddingBottom={0}
        >
          <HStack alignItems="center" justifyContent="space-between" width="100%" height={58}>
            <HStack alignItems="center" gap={4} flexShrink={0}>
              <PressableOpacity
                accessibilityLabel={NAVBAR_ACCESSIBILITY_LABELS.homeButton}
                onClick={handleHomeSelect}
                noScaleOnPress
              >
                <LogoMark size={32} />
              </PressableOpacity>
              {/* TODO: Investigate vertical bar visual bug reported in PR review - needs visual verification */}
              {!showMobileNavbar && isTokenizedEquitiesApp && (
                <HStack alignItems="center" gap={3} role="tablist" aria-label="Main navigation">
                  {tabs.map((tab, index) => (
                    <button
                      key={tab.id}
                      id={`nav-tab-${tab.id || 'explore'}`}
                      type="button"
                      role="tab"
                      aria-selected={selectedTab === tab.id}
                      aria-controls={`nav-tabpanel-${tab.id || 'explore'}`}
                      tabIndex={selectedTab === tab.id ? 0 : -1}
                      className={`${NavTabStyles} ${selectedTab === tab.id ? 'active' : ''}`}
                      onClick={() => handleTabChange(tab.id)}
                      onKeyDown={(e) => handleTabKeyDown(e, index)}
                    >
                      <TextHeadline as="span" color={selectedTab === tab.id ? 'fgPrimary' : 'fg'}>
                        {tab.label}
                      </TextHeadline>
                    </button>
                  ))}
                </HStack>
              )}
              {!showMobileNavbar && !isTokenizedEquitiesApp && (
                <TabNavigation
                  fontWeight="headline"
                  tabs={tabs.map((tab) => ({
                    ...tab,
                    label: renderTabLabel(tab),
                  }))}
                  value={selectedTab}
                  onChange={handleTabChange}
                  testID="navbar-tabs"
                />
              )}
            </HStack>

            <HStack gap={1} alignItems="center" flexShrink={0}>
              <Box>
                <NavbarAccountConnectButton />
              </Box>
              <NavbarSettingsDropdown
                accessibilityLabelThemeButton={NAVBAR_ACCESSIBILITY_LABELS.darkAndLightModeButton}
                theme={theme}
                toggleTheme={toggleTheme}
              />
              {showMobileNavbar &&
                (visible ? (
                  <IconButton
                    as="button"
                    name="close"
                    onClick={toggle}
                    accessibilityLabel={NAVBAR_ACCESSIBILITY_LABELS.closeNavigationMenuButton}
                  />
                ) : (
                  <IconButton
                    as="button"
                    name="menu"
                    onClick={toggle}
                    accessibilityLabel={NAVBAR_ACCESSIBILITY_LABELS.openNavigationMenuButton}
                  />
                ))}
            </HStack>
          </HStack>
        </NavigationBar>
      </Box>
      <Modal className={NavigationModalCss} visible={visible} onRequestClose={toggleOff}>
        <VStack alignItems="center" justifyContent="space-between" paddingY={1}>
          {tabItems.map((tab) => (
            <PressableOpacity
              key={tab.id}
              onClick={tab.handlePress}
              width="100%"
              noScaleOnPress
              accessibilityLabel={`${tab.label} page`}
            >
              <HStack
                key={tab.id}
                alignItems="center"
                paddingY={3}
                paddingX={3}
                gap={3}
                width="100%"
              >
                <Icon name={tab.icon} size="m" color="fg" />
                <TextHeadline as="span">{tab.label}</TextHeadline>
                {tab.isExternal && <Icon name="externalLink" size="s" color="fg" />}
              </HStack>
            </PressableOpacity>
          ))}
        </VStack>
      </Modal>
    </>
  );
}
